import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Banknote, Loader2, Unlock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTillStatus, notifyTillChanged, fmtHours } from '../hooks/useTillStatus';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shown each time the POS is opened while the till still needs opening (or yesterday's is still open). "Not now" lets staff sell.
export const TillOpenPrompt = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const { status, refresh } = useTillStatus();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [float, setFloat] = useState('200');
  const [saving, setSaving] = useState(false);

  const needsAttention = status && (status.phase === 'needs_open' || status.phase === 'stale');
  useEffect(() => { if (needsAttention && !dismissed) setOpen(true); }, [needsAttention, dismissed]);
  if (!needsAttention) return null;

  const openTill = async () => {
    const amount = parseFloat(float);
    if (Number.isNaN(amount) || amount < 0) { toast.error('Enter the float you counted'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/till/open`, { store: status.store, opening_float: amount, notes: 'Opened from POS' }, { headers: getAuthHeader() });
      toast.success(`Till opened with $${amount.toFixed(2)} float`);
      setOpen(false);
      notifyTillChanged();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open till');
    } finally { setSaving(false); }
  };

  const dismiss = () => { setDismissed(true); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="till-open-prompt">
        {status.phase === 'stale' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Yesterday's till is still open</DialogTitle>
              <DialogDescription>
                {status.store_name}'s till opened on {new Date(status.opened_on + 'T00:00:00').toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'short' })} was never closed. Count the cash in the drawer and close it, then open today's till with a fresh float.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={dismiss} data-testid="till-prompt-not-now">Not now</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => { setOpen(false); navigate('/till'); }} data-testid="till-prompt-go-close">Close yesterday's till</Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-800"><Banknote className="w-5 h-5" /> Have you opened the till?</DialogTitle>
              <DialogDescription>
                {status.store_name} opens at {fmtHours(status.hours?.open)}. Count the float in the drawer and enter it below so today's cash can be checked at close.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Counted float ($)</Label>
              <Input type="number" step="0.01" min="0" value={float} onChange={(e) => setFloat(e.target.value)} className="text-2xl h-14 text-center font-bold" data-testid="till-prompt-float" />
              <div className="grid grid-cols-4 gap-2">
                {[100, 150, 200, 250].map(a => (
                  <Button key={a} type="button" variant="outline" onClick={() => setFloat(String(a))} className={float === String(a) ? 'border-emerald-500 bg-emerald-50' : ''}>${a}</Button>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={dismiss} data-testid="till-prompt-not-now">Not now</Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={openTill} disabled={saving} data-testid="till-prompt-open">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />} Open till
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
